import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoginForm } from './components/auth/LoginForm';
import { SignUpForm } from './components/auth/SignUpForm';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MainLayout } from './components/layout/MainLayout';
import { SkipLink } from './components/ui/SkipLink';
import FeedPage from './components/feed/FeedPage';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { ProfilePage } from './components/profile/ProfilePage';
import { EditProfilePage } from './components/profile/EditProfilePage';
import { AccountSettings } from './components/settings/AccountSettings';
import { EventsPage } from './components/events/EventsPage';
import { EventDetails } from './components/events/EventDetails';
import { CreateEvent } from './components/events/CreateEvent';
import { GroupsPage } from './components/groups/GroupsPage';
import { GroupDetails } from './components/groups/GroupDetails';
import { CreateGroup } from './components/groups/CreateGroup';
import { DiscoverPage } from './components/discover/DiscoverPage';
import { TermsOfService } from './components/legal/TermsOfService';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { CookiePolicy } from './components/legal/CookiePolicy';
import { DisputeResolution } from './components/legal/DisputeResolution';
import { SEO } from './components/seo/SEO';
import { usePageTracking } from './lib/analytics/hooks';
import { ProductsPage } from './components/marketplace/ProductsPage';
import { CartPage } from './components/marketplace/CartPage';
import { CountdownPage } from './components/marketplace/CountdownPage';

function App() {
  // Track page views
  usePageTracking();

  return (
    <>
      <SEO />
      <SkipLink />
      <div 
        className="min-h-screen bg-gray-50 dark:bg-dark"
        role="application"
        aria-label="RoyaltySuite application"
      >
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginForm />} />
          <Route path="/signup" element={<SignUpForm />} />

          {/* Legal routes */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/dispute-resolution" element={<DisputeResolution />} />

          {/* Marketplace routes */}
          <Route
            path="/marketplace"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <CountdownPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/browse"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <ProductsPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/cart"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <CartPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <FeedPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <AdminDashboard />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <ProfilePage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <EditProfilePage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <AccountSettings />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <EventsPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/create"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <CreateEvent />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <EventDetails />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <GroupsPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/create"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <CreateGroup />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <GroupDetails />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/discover"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <main id="main-content" tabIndex={-1}>
                    <DiscoverPage />
                  </main>
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

export default App;